/**
 * Rig — the procedural humanoid skeleton.
 *
 * Conventions (these are load-bearing, everything else in src/characters/ assumes them):
 *
 *  - Metres, Y up, the character faces **+Z**. Feet on y = 0, head top at y = 1.80.
 *  - The bind pose is authored as a table of WORLD joint positions and every bone's
 *    bind rotation is IDENTITY. Local offsets are derived. That means a bone's local
 *    quaternion is literally "rotation away from the bind pose in world-aligned axes",
 *    which is what makes the procedural animation code in Anim.js readable: an elbow
 *    bend is a rotation about +X, a spine twist is a rotation about +Y.
 *  - The bind pose is a relaxed stand (arms down, slight A), never a T-pose.
 *
 *  Bone groups
 *    spine chain   root hips spine01 spine02 chest neck head headEnd
 *    arms          clavicleX upperArmX forearmX fTwistX handX handEndX
 *    legs          thighX shinX footX toeX
 *    dynamic       antennaA/B/C shemaghA/B holsterA beltA      (verlet-driven, see Secondary.js)
 *
 *  `fTwistX` is a skin-only twist helper hanging off the forearm. It carries half the
 *  wrist roll so the forearm does not candy-wrapper.
 *
 * owner: characters
 */
import * as THREE from 'three';

/** Bind pose: world position of every joint, metres. */
export const BIND_POS = {
  root: [0, 0, 0],
  hips: [0, 0.975, 0.000],
  spine01: [0, 1.085, 0.006],
  spine02: [0, 1.200, 0.012],
  chest: [0, 1.320, 0.006],
  // Head chain, re-proportioned. `head` is the atlanto-occipital pivot, `headEnd` the
  // crown: chin 1.556 -> crown 1.7935 is a 0.2375 m head on a 1.800 m soldier, i.e.
  // 7.58 heads. See the note at the top of Head.js — the old 0.277 m skull sitting
  // straight on the collar is what made the figure measure 5.0 heads in char_hero.
  neck: [0, 1.436, -0.006],
  head: [0, 1.552, 0.002],
  headEnd: [0, 1.7935, 0.006],

  clavicleL: [-0.042, 1.418, 0.020],
  upperArmL: [-0.180, 1.393, 0.004],
  forearmL: [-0.203, 1.113, -0.012],
  fTwistL: [-0.213, 0.985, -0.012],
  handL: [-0.222, 0.855, -0.012],
  handEndL: [-0.232, 0.762, -0.004],

  clavicleR: [0.042, 1.418, 0.020],
  upperArmR: [0.180, 1.393, 0.004],
  forearmR: [0.203, 1.113, -0.012],
  fTwistR: [0.213, 0.985, -0.012],
  handR: [0.222, 0.855, -0.012],
  handEndR: [0.232, 0.762, -0.004],

  thighL: [-0.094, 0.950, 0.004],
  shinL: [-0.104, 0.520, 0.016],
  footL: [-0.108, 0.098, -0.012],
  toeL: [-0.108, 0.032, 0.118],

  thighR: [0.094, 0.950, 0.004],
  shinR: [0.104, 0.520, 0.016],
  footR: [0.108, 0.098, -0.012],
  toeR: [0.108, 0.032, 0.118],

  // ---- dynamic (secondary motion) bones ----
  antennaA: [0.135, 1.330, -0.155],
  antennaB: [0.150, 1.560, -0.185],
  antennaC: [0.162, 1.775, -0.205],
  shemaghA: [0.000, 1.436, -0.080],
  shemaghB: [0.000, 1.296, -0.130],
  holsterA: [0.150, 0.700, 0.030],
  beltA: [-0.130, 0.930, 0.135],
};

/** parent table */
export const BONE_PARENT = {
  root: null,
  hips: 'root',
  spine01: 'hips',
  spine02: 'spine01',
  chest: 'spine02',
  neck: 'chest',
  head: 'neck',
  headEnd: 'head',

  clavicleL: 'chest', upperArmL: 'clavicleL', forearmL: 'upperArmL',
  fTwistL: 'forearmL', handL: 'forearmL', handEndL: 'handL',
  clavicleR: 'chest', upperArmR: 'clavicleR', forearmR: 'upperArmR',
  fTwistR: 'forearmR', handR: 'forearmR', handEndR: 'handR',

  thighL: 'hips', shinL: 'thighL', footL: 'shinL', toeL: 'footL',
  thighR: 'hips', shinR: 'thighR', footR: 'shinR', toeR: 'footR',

  antennaA: 'chest', antennaB: 'antennaA', antennaC: 'antennaB',
  shemaghA: 'neck', shemaghB: 'shemaghA',
  holsterA: 'thighR',
  beltA: 'hips',
};

/** Ordered so a parent always precedes its children. */
export const BONE_ORDER = [
  'root', 'hips', 'spine01', 'spine02', 'chest', 'neck', 'head', 'headEnd',
  'clavicleL', 'upperArmL', 'forearmL', 'fTwistL', 'handL', 'handEndL',
  'clavicleR', 'upperArmR', 'forearmR', 'fTwistR', 'handR', 'handEndR',
  'thighL', 'shinL', 'footL', 'toeL',
  'thighR', 'shinR', 'footR', 'toeR',
  'antennaA', 'antennaB', 'antennaC',
  'shemaghA', 'shemaghB',
  'holsterA', 'beltA',
];

/** Bones that the verlet solver owns (Anim must not write them). */
export const DYNAMIC_BONES = ['antennaA', 'antennaB', 'antennaC', 'shemaghA', 'shemaghB', 'holsterA', 'beltA'];

/** Upper-body mask used by the aim layer. 1 = fully driven by the layer. */
export const MASK_UPPER = {
  spine01: 0.35, spine02: 0.65, chest: 1, neck: 1, head: 1,
  clavicleL: 1, upperArmL: 1, forearmL: 1, handL: 1, fTwistL: 1,
  clavicleR: 1, upperArmR: 1, forearmR: 1, handR: 1, fTwistR: 1,
};

export const MASK_ARMS = {
  clavicleL: 1, upperArmL: 1, forearmL: 1, handL: 1, fTwistL: 1,
  clavicleR: 1, upperArmR: 1, forearmR: 1, handR: 1, fTwistR: 1,
};

export const MASK_LOWER = {
  hips: 1, thighL: 1, shinL: 1, footL: 1, toeL: 1,
  thighR: 1, shinR: 1, footR: 1, toeR: 1,
};

/** Hit region -> the bone the impulse is applied at, and the reaction clip to fire. */
export const REGION_BONE = {
  head: 'head',
  neck: 'neck',
  torso: 'chest',
  chest: 'chest',
  stomach: 'spine01',
  pelvis: 'hips',
  armL: 'upperArmL', armR: 'upperArmR',
  handL: 'handL', handR: 'handR',
  legL: 'thighL', legR: 'thighR',
  footL: 'footL', footR: 'footR',
};

/** Hit region -> the ragdoll bone the killing impulse goes into. */
export const REGION_RAGDOLL_BONE = {
  head: 'head', neck: 'head',
  torso: 'spine', chest: 'spine', stomach: 'spine',
  pelvis: 'pelvis',
  armL: 'upperArmL', armR: 'upperArmR',
  handL: 'lowerArmL', handR: 'lowerArmR',
  legL: 'thighL', legR: 'thighR',
  footL: 'shinL', footR: 'shinR',
};

/**
 * Ragdoll bone -> the skeleton bones its transform drives.
 *
 * `from`/`to` name the SKELETON joints whose bind-pose direction the capsule replaces.
 * `jf`/`jt` name the RAGDOLL joints (the particle names emitted by `buildRagdollSpec`)
 * whose positions give the capsule's world axis.
 *
 * Both are listed because `Ragdoll.bone(name).quaternion` cannot be trusted:
 * `refreshTransforms` builds its basis as `makeBasis(right, axis, front)` with
 * `front = axis × right`, which is LEFT-handed (a right-handed basis needs
 * `front = right × axis`). `Quaternion.setFromRotationMatrix` on a determinant −1
 * matrix returns a NON-UNIT quaternion — measured |q| between 0.88 and 0.96 — and a
 * non-unit quaternion on a THREE.Bone is a scale. Driving eleven bones with it shrank
 * the whole skeleton by |q|^(2·depth): the hip-to-head distance came out at 0.26 m
 * against a 0.57 m bind, which is the crushed body in the old char_ragdoll frame.
 * Deriving the axis from the particle positions sidesteps it entirely.
 * (Reported to physics — the fix there is one word in `refreshTransforms`.)
 */
export const RAGDOLL_DRIVE = [
  { rb: 'pelvis', bones: ['hips'], from: 'hips', to: 'spine01', jf: 'hip', jt: 'spineMid' },
  { rb: 'spine', bones: ['spine01', 'spine02', 'chest'], from: 'spine01', to: 'neck', jf: 'spineMid', jt: 'neck' },
  { rb: 'head', bones: ['neck', 'head'], from: 'neck', to: 'headEnd', jf: 'neck', jt: 'headTop' },
  { rb: 'upperArmL', bones: ['upperArmL'], from: 'upperArmL', to: 'forearmL', jf: 'shoulderL', jt: 'elbowL' },
  { rb: 'lowerArmL', bones: ['forearmL', 'handL'], from: 'forearmL', to: 'handL', jf: 'elbowL', jt: 'handL' },
  { rb: 'upperArmR', bones: ['upperArmR'], from: 'upperArmR', to: 'forearmR', jf: 'shoulderR', jt: 'elbowR' },
  { rb: 'lowerArmR', bones: ['forearmR', 'handR'], from: 'forearmR', to: 'handR', jf: 'elbowR', jt: 'handR' },
  { rb: 'thighL', bones: ['thighL'], from: 'thighL', to: 'shinL', jf: 'hipL', jt: 'kneeL' },
  { rb: 'shinL', bones: ['shinL', 'footL'], from: 'shinL', to: 'footL', jf: 'kneeL', jt: 'footL' },
  { rb: 'thighR', bones: ['thighR'], from: 'thighR', to: 'shinR', jf: 'hipR', jt: 'kneeR' },
  { rb: 'shinR', bones: ['shinR', 'footR'], from: 'shinR', to: 'footR', jf: 'kneeR', jt: 'footR' },
];

/**
 * The support hand's rotation relative to the weapon, shared by `Anim._armIK` (which
 * applies it to `handL`) and by `Body.buildHand` (which uses its inverse to author the
 * left-hand geometry in weapon space). One constant, so the fingers cannot drift off
 * the handguard the way two independently-tuned numbers always do.
 */
export const LEFT_GRIP_ROT = new THREE.Quaternion()
  .setFromEuler(new THREE.Euler(0.5, 0, 1.25, 'YXZ'));

/**
 * Where the carbine's landmarks sit, in the right hand's bind frame (metres, unscaled).
 * Gear.js builds the weapon around these; Body.js wraps the fingers around the same
 * numbers; Anim.js drives the arms onto them.
 */
export const WEAPON_LOCAL = {
  grip: [0, 0.052, 0.028],
  leftGrip: [0.002, 0.008, 0.203],
  muzzle: [0, 0.058, 0.452],
  sight: [0, 0.128, 0.106],
};

/** The same landmarks as world-scale bind-space vectors for a given rig. */
export function weaponAnchors(rig) {
  const h = rig.bind.handR, S = rig.scale;
  const P = (a) => new THREE.Vector3(h.x + a[0] * S, h.y + a[1] * S, h.z + a[2] * S);
  return {
    weaponGrip: P(WEAPON_LOCAL.grip),
    weaponLeftGrip: P(WEAPON_LOCAL.leftGrip),
    weaponMuzzle: P(WEAPON_LOCAL.muzzle),
    weaponSight: P(WEAPON_LOCAL.sight),
  };
}

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();

/**
 * Build a THREE.Bone hierarchy in the bind pose.
 * @returns {{
 *   rootBone: THREE.Bone, bones: THREE.Bone[], names: string[],
 *   byName: Map<string, THREE.Bone>, index: Map<string, number>,
 *   skeleton: THREE.Skeleton, bind: Record<string, THREE.Vector3>, scale: number,
 * }}
 */
export function createRig(scale = 1) {
  const bind = {};
  for (const k of BONE_ORDER) {
    const p = BIND_POS[k];
    bind[k] = new THREE.Vector3(p[0] * scale, p[1] * scale, p[2] * scale);
  }

  const byName = new Map();
  const bones = [];
  const index = new Map();
  for (const name of BONE_ORDER) {
    const b = new THREE.Bone();
    b.name = name;
    const parent = BONE_PARENT[name];
    if (parent) {
      _v.copy(bind[name]).sub(bind[parent]);
      b.position.copy(_v);
      byName.get(parent).add(b);
    } else {
      b.position.copy(bind[name]);
    }
    byName.set(name, b);
    index.set(name, bones.length);
    bones.push(b);
  }

  const rootBone = byName.get('root');
  rootBone.updateMatrixWorld(true);
  const skeleton = new THREE.Skeleton(bones);
  // bind rotations are identity by construction, so the inverses are exact
  skeleton.calculateInverses();

  return { rootBone, bones, names: BONE_ORDER.slice(), byName, index, skeleton, bind, scale };
}

/** Unit direction from `a` to `b` in the bind pose. */
export function bindDir(rig, a, b, out = new THREE.Vector3()) {
  return out.copy(rig.bind[b]).sub(rig.bind[a]).normalize();
}

export function bindLen(rig, a, b) {
  return _v2.copy(rig.bind[b]).sub(rig.bind[a]).length();
}

/**
 * Build a `physics.makeRagdoll` skeleton spec from the rig's CURRENT world pose.
 *
 * Joint positions are emitted relative to the hip joint (the spec's local frame) with
 * an identity rig quaternion, so the ragdoll spawns exactly on the animated pose —
 * that is what makes the 120 ms death blend invisible.
 *
 * @param {object} rig
 * @param {THREE.Object3D} root  character root (bones are children of it)
 * @param {object} opts { velocity, seed, mask, scale }
 */
export function buildRagdollSpec(rig, opts = {}) {
  const w = (n) => {
    const b = rig.byName.get(n);
    return b.getWorldPosition(new THREE.Vector3());
  };
  const s = rig.scale;
  const hip = w('hips');

  const P = {};
  const put = (key, vec) => { P[key] = [vec.x - hip.x, vec.y - hip.y, vec.z - hip.z]; };

  put('hip', hip);
  put('spineMid', w('spine02'));
  put('neck', w('neck'));
  put('headTop', w('headEnd'));
  put('shoulderL', w('upperArmL'));
  put('elbowL', w('forearmL'));
  put('handL', w('handL'));
  put('shoulderR', w('upperArmR'));
  put('elbowR', w('forearmR'));
  put('handR', w('handR'));
  put('hipL', w('thighL'));
  put('kneeL', w('shinL'));
  put('footL', w('footL'));
  put('hipR', w('thighR'));
  put('kneeR', w('shinR'));
  put('footR', w('footR'));

  // orientation reference particles: pushed out along the torso's own forward axis
  const chest = w('chest');
  const fwd = new THREE.Vector3();
  rig.byName.get('hips').getWorldDirection(fwd); // +Z of the bone
  if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, 1);
  fwd.normalize();
  put('hipFront', _v.copy(hip).addScaledVector(fwd, 0.14 * s).add(_v2.set(0, 0.02 * s, 0)));
  const cfwd = new THREE.Vector3();
  rig.byName.get('chest').getWorldDirection(cfwd);
  if (cfwd.lengthSq() < 1e-6) cfwd.set(0, 0, 1);
  put('chestFront', _v.copy(chest).addScaledVector(cfwd.normalize(), 0.13 * s));

  const bones = [
    { name: 'pelvis', from: 'hip', to: 'spineMid', radius: 0.150 * s, mass: 12 },
    { name: 'spine', from: 'spineMid', to: 'neck', radius: 0.140 * s, mass: 14 },
    { name: 'head', from: 'neck', to: 'headTop', radius: 0.105 * s, mass: 5 },
    { name: 'upperArmL', from: 'shoulderL', to: 'elbowL', radius: 0.060 * s, mass: 2.2 },
    { name: 'lowerArmL', from: 'elbowL', to: 'handL', radius: 0.050 * s, mass: 1.6 },
    { name: 'upperArmR', from: 'shoulderR', to: 'elbowR', radius: 0.060 * s, mass: 2.2 },
    { name: 'lowerArmR', from: 'elbowR', to: 'handR', radius: 0.050 * s, mass: 1.6 },
    { name: 'thighL', from: 'hipL', to: 'kneeL', radius: 0.092 * s, mass: 7 },
    { name: 'shinL', from: 'kneeL', to: 'footL', radius: 0.070 * s, mass: 4 },
    { name: 'thighR', from: 'hipR', to: 'kneeR', radius: 0.092 * s, mass: 7 },
    { name: 'shinR', from: 'kneeR', to: 'footR', radius: 0.070 * s, mass: 4 },
  ];

  const limits = [
    { joint: 'spineMid', parent: 'pelvis', child: 'spine', swing: 0.45, twist: 0.4 },
    { joint: 'neck', parent: 'spine', child: 'head', swing: 0.55, twist: 0.6 },
    { joint: 'shoulderL', parent: 'spine', child: 'upperArmL', swing: 1.25, twist: 1.0 },
    { joint: 'shoulderR', parent: 'spine', child: 'upperArmR', swing: 1.25, twist: 1.0 },
    { joint: 'elbowL', parent: 'upperArmL', child: 'lowerArmL', swing: 2.30, maxBend: 2.30 },
    { joint: 'elbowR', parent: 'upperArmR', child: 'lowerArmR', swing: 2.30, maxBend: 2.30 },
    { joint: 'hipL', parent: 'pelvis', child: 'thighL', swing: 1.05, twist: 0.55 },
    { joint: 'hipR', parent: 'pelvis', child: 'thighR', swing: 1.05, twist: 0.55 },
    { joint: 'kneeL', parent: 'thighL', child: 'shinL', swing: 2.15, maxBend: 2.15 },
    { joint: 'kneeR', parent: 'thighR', child: 'shinR', swing: 2.15, maxBend: 2.15 },
  ];

  const links = [
    ['neck', 'shoulderL', 1], ['neck', 'shoulderR', 1], ['shoulderL', 'shoulderR', 1],
    ['spineMid', 'shoulderL', 1], ['spineMid', 'shoulderR', 1],
    ['hip', 'hipL', 1], ['hip', 'hipR', 1], ['hipL', 'hipR', 1],
    ['spineMid', 'hipL', 1], ['spineMid', 'hipR', 1],
    ['hip', 'hipFront', 1], ['hipL', 'hipFront', 1], ['hipR', 'hipFront', 1],
    ['neck', 'chestFront', 1], ['shoulderL', 'chestFront', 1], ['shoulderR', 'chestFront', 1],
  ];

  return {
    position: hip.clone(),
    quaternion: [0, 0, 0, 1],
    scale: 1,
    velocity: opts.velocity || [0, 0, 0],
    angularKick: opts.angularKick ?? 0,
    seed: opts.seed ?? 1337,
    mask: opts.mask ?? 1,
    joints: P, bones, limits, links,
  };
}
